import { Injectable } from '@angular/core';
import { Appointment } from '../models';
import { HelperService } from './helper.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { DatabaseService } from './database.service';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {
  public userAppointmentList: Observable<any>;
  public userAppointmentListSubscription: Subscription;
  public userAppointments: Appointment[];
  public userAppointmentSubject = new Subject<any>();
  constructor(
    private dbService: DatabaseService,
    private helperService: HelperService,
  ) { }

  public loadUserAppointments(userId): void {
    this.userAppointmentList = this.getUserAppointmentsList(userId);
    this.userAppointmentListSubscription = this.userAppointmentList.subscribe(snap => {
      const list = this.helperService.transformFirebaseObjectToArray(snap);
      const appointments = [];
      let unread = 0;
      for (const item of list) {
        let appointment = new Appointment(item);
       appointments.push(appointment);
       if(appointment.status === "scheduled" && appointment.read === false) {
        unread++;
       }
      }
      //this.counterService.setCounter("unreadAppointments", unread);
      this.userAppointments = appointments;
      this.publishUserAppointmentSubject();
    });
  }

  private publishUserAppointmentSubject() {
    this.userAppointmentSubject.next(this.userAppointments);
  }

  public getUserAppointmentSubject(): Subject<any> {
    return this.userAppointmentSubject;
  }

  public clearUserAppointments() {
    this.userAppointments = null;
    this.userAppointmentList = null;
    if(this.userAppointmentListSubscription) {
      this.userAppointmentListSubscription.unsubscribe();
    }
  }

  public async getAppointmentAsPromise(appointmentId: string): Promise<Appointment | null> {
    const appt = await this.dbService.getSnapshot(`/appointments/${appointmentId}`);
    return appt? new Appointment({
      id: appointmentId,
      ...appt
    }) : null;
  }

  public async  getUserAppointmentAsPromise(userId: string, appointmentId: string): Promise<Appointment | null> {
    const appt = await this.dbService.getSnapshot(`/userAppointments/${userId}/${appointmentId}`);
    return appt? new Appointment({
      id: appointmentId,
      ...appt
    }) : null;
  }

  public async  getUserAppointmentsAsPromise(userId: string): Promise<Appointment[] | null> {
    const appt = await this.dbService.getSnapshot(`/userAppointments/${userId}`);
    return appt? this.helperService.transformFirebaseObjectToArray(appt).map(item => {return new Appointment(item)}) : null;
  }

  public addAppointment(appointment: Partial<Appointment>) {
    return this.dbService.pushByRef("appointments", appointment);
  }

  public updateAppointment(appointmentId: string, appointment: Partial<Appointment>) {
    return this.dbService.updateRef(`appointments/${appointmentId}`, appointment);
  }

  public updateUserAppointment(userId: string, appointmentId: string, appointment: any): Promise<any> {
    return this.dbService.updateRef(`userAppointments/${userId}/${appointmentId}`, appointment)
  }

  public cancelAppointment(appointmentId: string, occurrence?: string){
    if(occurrence) {
      return this.dbService.pushByRef(`appointments/${appointmentId}/recurring/exclusions`, {
        timestamp: occurrence
      });
    } else {
      return this.dbService.removeRef(`appointments/${appointmentId}`)
    }
  }

  public getUserAppointmentsList(userId: string) {
    return this.dbService.getListByRefTest(`userAppointments/${userId}`);
  }

  public setUserAppointmentRead(userId: string, appointmentId: string, val: boolean) {
    return this.dbService.setRef(`userAppointments/${userId}/${appointmentId}/read`, val);
  }

  public addException = (appointmentId: string, exception: Partial<Appointment>, originalOccurrence: string) => {
    return this.addAppointment({
      ...exception,
      parent: {
        id: appointmentId,
        occurrence: originalOccurrence
      }
    });
  }

  acceptEventInvitation(appointmentId: string, userId: string) {
    return this.dbService.updateRef(`appointments/${appointmentId}/invitees/${userId}`, {
      status: "accepted",
      acceptedOn: new Date().toISOString(),
      declinedOn: null
    });
  }

  declineEventInvitation(appointmentId: string, userId: string) {
    return this.dbService.updateRef(`appointments/${appointmentId}/invitees/${userId}`, {
      status: "declined",
      acceptedOn: null,
      declinedOn: new Date().toISOString(),
    });
  }
}
